import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HardDrive, AlertTriangle, CheckCircle, RefreshCw, Lock } from "lucide-react";
import { useTranslation } from "@/i18n";

const SUPER_PASSWORD = "okulus2024admin"; // TODO: Move to environment variable

export function StorageMonitoring() {
  const { t } = useTranslation();
  const [storageLimit, setStorageLimit] = useState<number>(100);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState("");

  const { data: storageUsage, refetch, isLoading } = trpc.settings.getStorageUsage.useQuery();
  const updateLimitMutation = trpc.settings.updateStorageLimit.useMutation({
    onSuccess: () => {
      toast.success(t.settings.storageLimitUpdated || "Speicherlimit aktualisiert");
      setIsEditing(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Fehler beim Aktualisieren");
    },
  });

  useEffect(() => {
    if (storageUsage) {
      setStorageLimit(storageUsage.limitGB);
    }
  }, [storageUsage]);

  const handleSaveLimit = () => {
    if (storageLimit < 1) {
      toast.error("Limit muss mindestens 1 GB sein");
      return;
    }
    updateLimitMutation.mutate({ uploadStorageLimitGB: storageLimit });
  };

  const formatSize = (gb: number) => {
    if (gb < 1) {
      return `${(gb * 1024).toFixed(0)} MB`;
    }
    return `${gb.toFixed(2)} GB`;
  };

  const getStatusColor = () => {
    if (!storageUsage) return "bg-gray-500";
    if (storageUsage.isOverLimit) return "bg-red-500";
    if (storageUsage.usagePercent > 80) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStatusIcon = () => {
    if (!storageUsage) return <HardDrive className="h-5 w-5" />;
    if (storageUsage.isOverLimit) return <AlertTriangle className="h-5 w-5 text-red-500" />;
    if (storageUsage.usagePercent > 80) return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  };

  return (
    <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="h-5 w-5 text-purple-600" />
          {t.settings.storageMonitoring || "Speicher-Monitoring"}
        </CardTitle>
        <CardDescription>
          {t.settings.storageMonitoringDescription || "Überwachen Sie Ihre Upload-Speichernutzung"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="text-center text-muted-foreground py-8">
            {t.common.loading || "Lädt..."}
          </div>
        ) : storageUsage ? (
          <>
            {/* Storage Usage Display */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon()}
                  <span className="text-lg font-semibold">
                    {formatSize(storageUsage.totalGB)} / {formatSize(storageUsage.limitGB)}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  {t.common.refresh || "Aktualisieren"}
                </Button>
              </div>

              <Progress 
                value={Math.min(storageUsage.usagePercent, 100)} 
                className="h-3"
              />

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">{t.settings.storageUsed || "Verwendet"}</p>
                  <p className="font-semibold">{formatSize(storageUsage.totalGB)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t.settings.storageRemaining || "Verfügbar"}</p>
                  <p className="font-semibold">{formatSize(storageUsage.remaining)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t.settings.storageUsagePercent || "Auslastung"}</p>
                  <p className="font-semibold">{storageUsage.usagePercent.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t.settings.storageLimit || "Limit"}</p>
                  <p className="font-semibold">{formatSize(storageUsage.limitGB)}</p>
                </div>
              </div>

              {storageUsage.isOverLimit && (
                <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-semibold">
                      {t.settings.storageOverLimit || "Speicherlimit überschritten!"}
                    </span>
                  </div>
                  <p className="text-sm text-red-600 dark:text-red-300 mt-2">
                    {t.settings.storageOverLimitMessage || "Bitte erhöhen Sie das Limit oder löschen Sie Dateien."}
                  </p>
                </div>
              )}

              {storageUsage.usagePercent > 80 && !storageUsage.isOverLimit && (
                <div className="bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-semibold">
                      {t.settings.storageNearLimit || "Speicher fast voll"}
                    </span>
                  </div>
                  <p className="text-sm text-yellow-600 dark:text-yellow-300 mt-2">
                    {t.settings.storageNearLimitMessage || "Erwägen Sie, das Limit zu erhöhen."}
                  </p>
                </div>
              )}
            </div>

            {/* Storage Limit Configuration */}
            <div className="border-t pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="storageLimit" className="text-base font-semibold">
                  {t.settings.configureStorageLimit || "Speicherlimit konfigurieren"}
                </Label>
                {!isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPasswordDialog(true)}
                    className="gap-2"
                  >
                    <Lock className="h-4 w-4" />
                    {t.common.edit || "Bearbeiten"}
                  </Button>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-4">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="storageLimit">{t.settings.storageLimitGB || "Limit (GB)"}</Label>
                      <Input
                        id="storageLimit"
                        type="number"
                        min={1}
                        max={10000}
                        step={1}
                        value={storageLimit}
                        onChange={(e) => setStorageLimit(parseFloat(e.target.value))}
                      />
                    </div>
                    <Button
                      onClick={handleSaveLimit}
                      disabled={updateLimitMutation.isPending}
                    >
                      {updateLimitMutation.isPending ? t.common.saving : t.common.save}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        setStorageLimit(storageUsage.limitGB);
                      }}
                    >
                      {t.common.cancel || "Abbrechen"}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t.settings.storageLimitHelp || "Setzen Sie das maximale Speicherlimit für Uploads in Gigabyte."}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t.settings.currentLimit || "Aktuelles Limit"}: <span className="font-semibold">{formatSize(storageUsage.limitGB)}</span>
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            {t.settings.storageLoadError || "Fehler beim Laden der Speicherinformationen"}
          </div>
        )}

        {/* Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                {t.settings.storageLimitPasswordTitle || "Potrebna je super lozinka"}
              </DialogTitle>
              <DialogDescription>
                {t.settings.storageLimitPasswordDescription ||
                  "Unesite super lozinku za promjenu limita pohrane."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="password">{t.settings.password || "Passwort"}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (password === SUPER_PASSWORD) {
                        setIsEditing(true);
                        setShowPasswordDialog(false);
                        setPassword("");
                        toast.success(t.settings.passwordCorrect || "Zugriff gewährt");
                      } else {
                        toast.error(t.settings.passwordIncorrect || "Falsches Passwort");
                        setPassword("");
                      }
                    }
                  }}
                  placeholder="●●●●●●●●"
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasswordDialog(false);
                  setPassword("");
                }}
              >
                {t.common.cancel || "Abbrechen"}
              </Button>
              <Button
                onClick={() => {
                  if (password === SUPER_PASSWORD) {
                    setIsEditing(true);
                    setShowPasswordDialog(false);
                    setPassword("");
                    toast.success(t.settings.passwordCorrect || "Zugriff gewährt");
                  } else {
                    toast.error(t.settings.passwordIncorrect || "Falsches Passwort");
                    setPassword("");
                  }
                }}
              >
                {t.common.confirm || "Bestätigen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
